import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { Upload, FileText, Trash2, Play, Loader2, X, FileSearch } from "lucide-react";

export default function GapAnalysis() {
  const [docs, setDocs] = useState([]);
  const [run, setRun] = useState(null);
  const [tab, setTab] = useState("docs");
  const [running, setRunning] = useState(false);
  const [paste, setPaste] = useState({ title: "", content: "" });
  const [uploading, setUploading] = useState(false);
  const [active, setActive] = useState(null); // verdict dialog

  const loadDocs = useCallback(() => api.get("/documents").then((r) => setDocs(r.data)), []);

  const pollRun = useCallback(async () => {
    const { data } = await api.get("/gap/latest");
    setRun(data);
    if (data && data.status === "running") { setRunning(true); setTimeout(pollRun, 4000); return; }
    setRunning(false);
    if (data && data.status === "complete") return data;
  }, []);

  useEffect(() => { loadDocs(); pollRun(); }, [loadDocs, pollRun]);

  const submitPaste = async () => {
    if (!paste.title.trim() || !paste.content.trim()) return toast.error("Add a title and content.");
    try {
      await api.post("/documents/paste", paste);
      setPaste({ title: "", content: "" });
      toast.success("Document ingested.");
      loadDocs();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", file.name);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File ingested.");
      loadDocs();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not parse file"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const removeDoc = async (id) => { await api.delete(`/documents/${id}`); loadDocs(); };

  const runAnalysis = async () => {
    setRunning(true);
    try {
      await api.post("/gap/run");
      toast.message("Analysis started — this runs in the background (~40s).");
      setTab("results");
      setTimeout(pollRun, 3000);
    } catch (e) { toast.error(e.response?.data?.detail || "Analysis failed"); setRunning(false); }
  };

  // group verdicts by family
  const ready = run && run.status === "complete";
  const grouped = {};
  (ready ? run.verdicts : []).forEach((v) => { (grouped[v.family] ||= []).push(v); });

  return (
    <div className="p-8 max-w-7xl">
      <div className="mb-6">
        <div className="overline mb-1">Feature A</div>
        <h1 className="font-display font-bold text-3xl tracking-tight">Gap Analysis</h1>
        <p className="text-slate-500 text-sm mt-1">AI maps your evidence to NIST 800-171A objectives with cited verdicts.</p>
      </div>

      <div className="flex gap-1 border-b border-slate-200 mb-6">
        {[["docs", "Evidence Documents"], ["results", "Results"], ["poam", "POA&M"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? "border-brand-accent text-brand" : "border-transparent text-slate-500 hover:text-brand"}`}>
            {l}
          </button>
        ))}
        <div className="flex-1" />
        <button onClick={runAnalysis} disabled={running || docs.length === 0} data-testid="run-analysis-btn"
          className="bg-brand text-white px-5 py-2 rounded-md text-sm font-medium flex items-center gap-2 hover:bg-brand-secondary transition-colors disabled:opacity-50 mb-1">
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} {running ? "Analyzing…" : "Run Analysis"}
        </button>
      </div>

      {tab === "docs" && (
        <DocumentsTab docs={docs} paste={paste} setPaste={setPaste} uploading={uploading}
          onSubmitPaste={submitPaste} onUpload={uploadFile} onRemove={removeDoc} />
      )}

      {tab === "results" && (
        <ResultsTab running={running} ready={ready} run={run} grouped={grouped} onSelect={setActive} />
      )}

      {tab === "poam" && <Poam />}

      {active && <VerdictDialog v={active} onClose={() => setActive(null)} />}
    </div>
  );
}

function DocumentsTab({ docs, paste, setPaste, uploading, onSubmitPaste, onUpload, onRemove }) {
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white border border-slate-200 rounded-md p-6 shadow-sm">
        <h3 className="font-display font-bold text-lg mb-4">Add evidence</h3>
        <label className="text-sm font-medium text-slate-700">Document title</label>
        <input value={paste.title} onChange={(e) => setPaste({ ...paste, title: e.target.value })} data-testid="paste-title"
          placeholder="e.g. Access Control Policy" className="w-full mt-1 mb-3 px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-brand-accent focus:outline-none" />
        <label className="text-sm font-medium text-slate-700">Paste policy / config / log text</label>
        <textarea value={paste.content} onChange={(e) => setPaste({ ...paste, content: e.target.value })} data-testid="paste-content"
          rows={6} className="w-full mt-1 mb-3 px-3 py-2 border border-slate-300 rounded-md font-mono text-xs focus:ring-2 focus:ring-brand-accent focus:outline-none" />
        <div className="flex items-center gap-3">
          <button onClick={onSubmitPaste} data-testid="paste-submit" className="bg-brand text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-brand-secondary transition-colors">Add text</button>
          <span className="text-slate-400 text-sm">or</span>
          <label className="cursor-pointer inline-flex items-center gap-2 border border-slate-300 px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-50 transition-colors" data-testid="upload-label">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload file
            <input type="file" className="hidden" onChange={onUpload} accept=".pdf,.docx,.txt,.csv,.json,.md,.log" data-testid="upload-input" />
          </label>
        </div>
        <p className="text-xs text-slate-400 mt-2">Supports PDF, DOCX, TXT, CSV, JSON, MD, LOG.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm">
        <div className="px-6 py-4 border-b border-slate-200 font-display font-bold">Ingested documents ({docs.length})</div>
        <div className="divide-y divide-slate-100 max-h-[420px] overflow-auto">
          {docs.length === 0 && <div className="p-6 text-sm text-slate-500">No documents yet.</div>}
          {docs.map((d) => (
            <div key={d.id} className="px-6 py-3 flex items-center justify-between" data-testid={`doc-${d.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{d.title}</div>
                  <div className="text-xs text-slate-400 font-mono">{d.chunk_count} chunks · {d.char_count} chars</div>
                </div>
              </div>
              <button onClick={() => onRemove(d.id)} className="text-slate-400 hover:text-danger" data-testid={`del-doc-${d.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ResultsTab({ running, ready, run, grouped, onSelect }) {
  if (running)
    return (
      <div className="bg-white border border-slate-200 rounded-md p-10 text-center text-slate-500 shadow-sm flex flex-col items-center gap-3" data-testid="run-progress">
        <Loader2 className="h-8 w-8 animate-spin text-brand-accent" />
        Analyzing your evidence against the NIST 800-171A objectives…
      </div>
    );
  if (!ready) return <Empty msg="No analysis yet. Add documents and click Run Analysis." />;
  return (
    <div className="space-y-5">
      <div className="flex gap-4">
        <Metric label="SPRS Score" value={run.sprs_score} />
        <Metric label="Readiness" value={`${run.readiness_pct}%`} />
        <Metric label="Met" value={`${run.met}/${run.total}`} />
      </div>
      {Object.entries(grouped).map(([fam, items]) => (
        <div key={fam} className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 font-semibold text-sm">{fam} Family <span className="text-slate-400 font-normal">· {items.length} objectives</span></div>
          <table className="w-full text-sm">
            <tbody>
              {items.map((v) => (
                <tr key={v.id} onClick={() => onSelect(v)} data-testid={`verdict-${v.id}`}
                  className={`border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50 ${v.status === "NOT_MET" ? "bg-red-50/40" : ""}`}>
                  <td className="px-5 py-3 font-mono text-xs w-36 text-slate-700">{v.id}</td>
                  <td className="px-3 py-3 text-slate-700">{v.requirement_text.slice(0, 90)}…</td>
                  <td className="px-3 py-3 w-24 text-right text-xs text-slate-400">{v.citations?.length || 0} cite(s)</td>
                  <td className="px-5 py-3 w-32 text-right"><StatusBadge status={v.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md px-6 py-4 shadow-sm flex-1">
      <div className="overline mb-1">{label}</div>
      <div className="font-display font-black text-2xl">{value}</div>
    </div>
  );
}

function Empty({ msg }) {
  return <div className="bg-white border border-slate-200 rounded-md p-10 text-center text-slate-500 shadow-sm flex flex-col items-center gap-3"><FileSearch className="h-8 w-8 text-slate-300" />{msg}</div>;
}

function VerdictDialog({ v, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="verdict-dialog">
      <div className="bg-white rounded-md max-w-2xl w-full max-h-[85vh] overflow-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div className="font-mono text-sm font-semibold">{v.id}</div>
          <div className="flex items-center gap-3"><StatusBadge status={v.status} /><button onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button></div>
        </div>
        <div className="p-6 space-y-4">
          <div><div className="overline mb-1">Requirement</div><p className="text-sm text-slate-700">{v.requirement_text}</p></div>
          <div><div className="overline mb-1">Assessment objective</div><p className="text-sm text-slate-600">{v.assessment_objective_text}</p></div>
          <div><div className="overline mb-1">Assessor rationale</div><p className="text-sm text-slate-700 italic">{v.rationale || "—"}</p>
            <div className="text-xs text-slate-400 mt-1">Confidence: {Math.round((v.confidence || 0) * 100)}% · SPRS weight: {v.sprs_weight}</div></div>
          <div>
            <div className="overline mb-2">Cited evidence ({v.citations?.length || 0})</div>
            {(!v.citations || v.citations.length === 0) ? (
              <div className="text-sm text-slate-400">No supporting evidence cited.</div>
            ) : v.citations.map((c, i) => (
              <div key={`${c.source_title}-${c.line_start}-${i}`} className="border border-slate-200 rounded-md p-3 mb-2 bg-slate-50" data-testid={`citation-${i}`}>
                <div className="text-xs font-semibold text-slate-700">{c.source_title} <span className="text-slate-400 font-mono font-normal">· lines {c.line_start}-{c.line_end}</span></div>
                <p className="text-xs text-slate-600 mt-1 font-mono">&ldquo;{c.snippet}&rdquo;</p>
                <div className="text-[10px] text-slate-400 font-mono mt-1 truncate">sha256: {c.sha256}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Poam() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/gap/poam").then((r) => setItems(r.data)); }, []);
  if (!items) return <div className="text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  if (items.length === 0) return <Empty msg="No open items. Run an analysis to generate your POA&M." />;
  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden" data-testid="poam-table">
      <table className="w-full text-sm">
        <thead><tr className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
          <th className="px-5 py-3">Control</th><th className="px-3 py-3">Family</th><th className="px-3 py-3">Requirement</th>
          <th className="px-3 py-3 text-right">SPRS pts</th><th className="px-5 py-3 text-right">Status</th>
        </tr></thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.objective_id} className="border-b border-slate-100 last:border-0" data-testid={`poam-${it.objective_id}`}>
              <td className="px-5 py-3 font-mono text-xs">{it.objective_id}</td>
              <td className="px-3 py-3 text-slate-600 text-xs">{it.family}</td>
              <td className="px-3 py-3 text-slate-700">{it.requirement.slice(0, 80)}…</td>
              <td className="px-3 py-3 text-right font-mono font-semibold text-danger">-{it.sprs_points}</td>
              <td className="px-5 py-3 text-right"><StatusBadge status={it.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
